import type { SupabaseClient } from "@supabase/supabase-js";

import {
  asColorToken,
  asOpenFillMode,
  type ColorToken,
  type OpenFillMode,
} from "../../../../packages/shared/src/schedule-color.ts";

// Scheduling read models - public.schedule_coverage + public.my_shifts +
// public.management_shift_response_log (Codex migrations 20260610151545,
// 20260610151603, 20260610174609; hosted). These are security_invoker views:
// coverage and management responses are permission-scoped server-side; my_shifts returns
// ONLY the caller's own assignments (auth.uid()). Read-only by design - assignment creation,
// publishing, and guard self-serve responses arrive through separate workflows.

// `shift_template_id` is read so the publish review can match existing slots to the template
// the way the RPC itself does (account + template + slot_date + starts_at). It has been in
// the view since 20260610170919, so the pre-6A fallback list below can carry it too.
export const SCHEDULE_COVERAGE_COLUMNS =
  "shift_slot_id, shift_template_id, site_name, route_name, slot_date, starts_at, ends_at, headcount_required, assigned_count, coverage_status, slot_status, required_credential_types, declined_count, decline_reasons";

/**
 * Wave 6A projects the template's color and effective fill mode onto every coverage row
 * (`coalesce(template.color_token, 'slate')`). Tried first; a pre-6A server answers 42703
 * and the legacy list is re-read so the board never goes blank over missing decoration.
 */
export const SCHEDULE_COVERAGE_COLUMNS_6B = `${SCHEDULE_COVERAGE_COLUMNS}, color_token, open_fill_mode`;

const UNDEFINED_COLUMN = "42703";

export const MY_SHIFTS_COLUMNS =
  "route_assignment_id, route_name, site_name, slot_date, starts_at, ends_at, assignment_status, response_status, decline_reason, responded_at, required_credential_types";

export const SHIFT_RESPONSE_LOG_COLUMNS =
  "shift_response_id, route_assignment_id, guard_label, assigned_by_label, shift_slot_id, slot_date, route_id, route_name, site_id, site_name, starts_at, ends_at, assignment_status, response_status, decline_reason, responded_at, required_credential_types, created_at, updated_at";

export const COVERAGE_STATUSES = ["filled", "partial", "open"] as const;
export const SLOT_STATUSES = ["draft", "published", "cancelled"] as const;

export interface ScheduleCoverageRow {
  shiftSlotId: string;
  /** The template that produced this slot, or null for a hand-made one. */
  shiftTemplateId: string | null;
  siteName: string | null;
  routeName: string | null;
  slotDate: string | null;
  startsAt: string | null;
  endsAt: string | null;
  headcountRequired: number;
  assignedCount: number;
  declinedCount: number;
  declineReasons: string[];
  /** 'filled' | 'partial' | 'open' from the view; 'unknown' on a malformed row. */
  coverageStatus: string;
  /** 'draft' | 'published' | 'cancelled' from the view; 'unknown' on a malformed row. */
  slotStatus: string;
  requiredCredentialTypes: string[];
  /** Template palette token, inherited via the view; `slate` when absent or unknown. */
  colorToken: ColorToken;
  /** Effective fill policy, inherited via the view; `instant_claim` when absent. */
  openFillMode: OpenFillMode;
}

export interface MyShiftRow {
  routeAssignmentId: string;
  routeName: string | null;
  siteName: string | null;
  slotDate: string | null;
  startsAt: string | null;
  endsAt: string | null;
  assignmentStatus: string;
  /** 'none' | 'acknowledged' | 'declined' (view coalesces absent responses to 'none'). */
  responseStatus: string;
  declineReason: string | null;
  respondedAt: string | null;
  requiredCredentialTypes: string[];
}

export interface ShiftResponseLogRow {
  shiftResponseId: string;
  routeAssignmentId: string;
  guardLabel: string;
  assignedByLabel: string | null;
  shiftSlotId: string | null;
  slotDate: string | null;
  routeId: string | null;
  routeName: string | null;
  siteId: string | null;
  siteName: string | null;
  startsAt: string | null;
  endsAt: string | null;
  assignmentStatus: string;
  /** Latest response state from public.management_shift_response_log. */
  responseStatus: string;
  declineReason: string | null;
  respondedAt: string | null;
  requiredCredentialTypes: string[];
  createdAt: string | null;
  updatedAt: string | null;
}

function asString(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function asCount(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function asStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : [];
}

export function mapScheduleCoverageRow(raw: Record<string, unknown>): ScheduleCoverageRow {
  return {
    shiftSlotId: asString(raw.shift_slot_id) ?? "",
    shiftTemplateId: asString(raw.shift_template_id),
    siteName: asString(raw.site_name),
    routeName: asString(raw.route_name),
    slotDate: asString(raw.slot_date),
    startsAt: asString(raw.starts_at),
    endsAt: asString(raw.ends_at),
    headcountRequired: asCount(raw.headcount_required),
    assignedCount: asCount(raw.assigned_count),
    declinedCount: asCount(raw.declined_count),
    declineReasons: asStringArray(raw.decline_reasons),
    coverageStatus: asString(raw.coverage_status) ?? "unknown",
    slotStatus: asString(raw.slot_status) ?? "unknown",
    requiredCredentialTypes: asStringArray(raw.required_credential_types),
    // Unknown token -> slate, so an unvalidated server string never reaches a style value.
    colorToken: asColorToken(raw.color_token),
    openFillMode: asOpenFillMode(raw.open_fill_mode),
  };
}

export function mapMyShiftRow(raw: Record<string, unknown>): MyShiftRow {
  return {
    routeAssignmentId: asString(raw.route_assignment_id) ?? "",
    routeName: asString(raw.route_name),
    siteName: asString(raw.site_name),
    slotDate: asString(raw.slot_date),
    startsAt: asString(raw.starts_at),
    endsAt: asString(raw.ends_at),
    assignmentStatus: asString(raw.assignment_status) ?? "unknown",
    responseStatus: asString(raw.response_status) ?? "none",
    declineReason: asString(raw.decline_reason),
    respondedAt: asString(raw.responded_at),
    requiredCredentialTypes: asStringArray(raw.required_credential_types),
  };
}

export function mapShiftResponseLogRow(raw: Record<string, unknown>): ShiftResponseLogRow {
  return {
    shiftResponseId: asString(raw.shift_response_id) ?? "",
    routeAssignmentId: asString(raw.route_assignment_id) ?? "",
    guardLabel: asString(raw.guard_label) ?? "Guard unknown",
    assignedByLabel: asString(raw.assigned_by_label),
    shiftSlotId: asString(raw.shift_slot_id),
    slotDate: asString(raw.slot_date),
    routeId: asString(raw.route_id),
    routeName: asString(raw.route_name),
    siteId: asString(raw.site_id),
    siteName: asString(raw.site_name),
    startsAt: asString(raw.starts_at),
    endsAt: asString(raw.ends_at),
    assignmentStatus: asString(raw.assignment_status) ?? "unknown",
    responseStatus: asString(raw.response_status) ?? "unknown",
    declineReason: asString(raw.decline_reason),
    respondedAt: asString(raw.responded_at),
    requiredCredentialTypes: asStringArray(raw.required_credential_types),
    createdAt: asString(raw.created_at),
    updatedAt: asString(raw.updated_at),
  };
}

export const SCHEDULE_COVERAGE_ROW_LIMIT = 200;
export const MY_SHIFTS_ROW_LIMIT = 50;
export const SHIFT_RESPONSE_LOG_ROW_LIMIT = 100;

/**
 * Week-window coverage for the Builder grid: slots whose slot_date falls inside [fromDate,
 * toDate] inclusive. Read-only like everything in this module - the Builder scaffold makes
 * zero mutation calls until the write contracts land.
 */
export async function fetchScheduleCoverageRange(
  client: SupabaseClient,
  accountId: string,
  fromDate: string,
  toDate: string,
  limit = 500,
): Promise<ScheduleCoverageRow[]> {
  const load = await readCoverage(
    (columns) =>
      client
        .from("schedule_coverage")
        .select(columns)
        .eq("account_id", accountId)
        .gte("slot_date", fromDate)
        .lte("slot_date", toDate)
        .order("starts_at", { ascending: true })
        .limit(limit),
    "Could not load the schedule week.",
  );
  return load.rows;
}

export interface ScheduleCoverageLoad {
  readonly rows: ScheduleCoverageRow[];
  /** True when this server predates 6A: every row reads slate / instant claim. */
  readonly degraded: boolean;
}

function isUndefinedColumn(error: unknown): boolean {
  if (error === null || typeof error !== "object") return false;
  return (error as { code?: unknown }).code === UNDEFINED_COLUMN;
}

function mapCoverageRows(data: unknown): ScheduleCoverageRow[] {
  const rows = Array.isArray(data) ? (data as unknown[]) : [];
  return rows
    .filter((row): row is Record<string, unknown> => typeof row === "object" && row !== null)
    .map(mapScheduleCoverageRow)
    .filter((row) => row.shiftSlotId !== "");
}

/** Extended select first; only a 42703 earns the legacy re-read. Anything else throws. */
async function readCoverage(
  run: (columns: string) => PromiseLike<{ data: unknown; error: unknown }>,
  failure: string,
): Promise<ScheduleCoverageLoad> {
  const extended = await run(SCHEDULE_COVERAGE_COLUMNS_6B);
  if (!extended.error) {
    return { rows: mapCoverageRows(extended.data), degraded: false };
  }
  if (!isUndefinedColumn(extended.error)) {
    throw new Error(failure);
  }
  const legacy = await run(SCHEDULE_COVERAGE_COLUMNS);
  if (legacy.error) {
    throw new Error(failure);
  }
  return { rows: mapCoverageRows(legacy.data), degraded: true };
}

/**
 * Upcoming + in-progress coverage, soonest first. `fromIso` keeps finished slots out of the
 * report (ends_at >= from) - pass "now" from the caller so the window is testable.
 */
export async function fetchScheduleCoverage(
  client: SupabaseClient,
  accountId: string,
  fromIso: string,
  limit: number = SCHEDULE_COVERAGE_ROW_LIMIT,
): Promise<ScheduleCoverageLoad> {
  return readCoverage(
    (columns) =>
      client
        .from("schedule_coverage")
        .select(columns)
        .eq("account_id", accountId)
        .gte("ends_at", fromIso)
        .order("starts_at", { ascending: true })
        .limit(limit),
    "Could not load schedule coverage.",
  );
}

/**
 * The CALLER's own active/upcoming shifts, soonest first - the view enforces own-rows-only
 * server-side. `fromIso` keeps stale assignments from crowding the guard preview.
 */
export async function fetchMyShifts(
  client: SupabaseClient,
  accountId: string,
  fromIso: string,
  limit: number = MY_SHIFTS_ROW_LIMIT,
): Promise<MyShiftRow[]> {
  const { data, error } = await client
    .from("my_shifts")
    .select(MY_SHIFTS_COLUMNS)
    .eq("account_id", accountId)
    .in("assignment_status", ["scheduled", "active"])
    .gte("ends_at", fromIso)
    .order("starts_at", { ascending: true })
    .limit(limit);
  if (error) {
    throw new Error("Could not load your shifts.");
  }
  const rows = Array.isArray(data) ? (data as unknown[]) : [];
  return rows
    .filter((row): row is Record<string, unknown> => typeof row === "object" && row !== null)
    .map(mapMyShiftRow)
    .filter((row) => row.routeAssignmentId !== "");
}

/**
 * Latest guard shift acknowledgements/declines for supervisors. The hosted view is already
 * management-safe and redacted; this client keeps the select list explicit and displays only
 * labels/context, never raw users, email, phone, notes, GPS, tokens, or payloads.
 */
export async function fetchShiftResponseLog(
  client: SupabaseClient,
  accountId: string,
  limit: number = SHIFT_RESPONSE_LOG_ROW_LIMIT,
): Promise<ShiftResponseLogRow[]> {
  const { data, error } = await client
    .from("management_shift_response_log")
    .select(SHIFT_RESPONSE_LOG_COLUMNS)
    .eq("account_id", accountId)
    .order("responded_at", { ascending: false, nullsFirst: false })
    .limit(limit);
  if (error) {
    throw new Error("Could not load shift responses.");
  }
  const rows = Array.isArray(data) ? (data as unknown[]) : [];
  return rows
    .filter((row): row is Record<string, unknown> => typeof row === "object" && row !== null)
    .map(mapShiftResponseLogRow)
    .filter((row) => row.shiftResponseId !== "");
}
